import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import type { Permission, Role } from '@paroquia/shared'
import { ApiError, apiGet, apiPost } from './api'

export interface SessionUser {
  id: string
  name: string
  email: string
  role: Role
  roleLabel: string
  mustChangePassword: boolean
}

export interface SessionData {
  user: SessionUser
  permissions: Permission[]
  eventIds: string[]
  session: { expiresAt: string }
}

interface AuthContextValue {
  session: SessionData | null
  isLoading: boolean
  /** Permissao global. Para permissao com escopo de evento, passe o eventId. */
  can: (permission: Permission, eventId?: string) => boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const SESSION_QUERY_KEY = ['auth', 'me'] as const

/**
 * Papeis com alcance paroquial. Espelha PARISH_WIDE_ROLES do pacote shared.
 *
 * Esta verificacao no cliente serve apenas para ESCONDER o que o usuario nao
 * pode usar. A decisao que vale e a do servidor (requirePermission); aqui e
 * conforto de interface, nunca seguranca.
 */
const PAPEIS_PAROQUIAIS: readonly Role[] = ['ADMIN', 'SECRETARIA', 'TESOURARIA']

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const query: UseQueryResult<SessionData | null> = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiGet<SessionData>('/api/auth/me')
      } catch (error) {
        // 401 aqui e o caso normal do visitante anonimo, nao uma falha.
        if (error instanceof ApiError && error.isUnauthorized) return null
        throw error
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const session = query.data ?? null

  const can = useCallback(
    (permission: Permission, eventId?: string): boolean => {
      if (!session) return false
      if (!session.permissions.includes(permission)) return false
      // Coordenador de evento so alcanca os eventos de que participa.
      if (!PAPEIS_PAROQUIAIS.includes(session.user.role) && eventId) {
        return session.eventIds.includes(eventId)
      }
      return true
    },
    [session],
  )

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
  }, [queryClient])

  const logout = useCallback(async () => {
    await apiPost('/api/auth/logout')
    // Limpa todo o cache, nao so a sessao: listas de inscritos e balancetes
    // ficariam em memoria e visiveis ao proximo usuario da mesma maquina -
    // cenario real no computador compartilhado da secretaria.
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({ session, isLoading: query.isLoading, can, refresh, logout }),
    [session, query.isLoading, can, refresh, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>')
  return context
}
